import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import { UseInfiniteQueryResult, useInfiniteQuery } from 'react-query';
import { PaginatedMemberReportList, UrlParams } from 'types';
import { client } from 'utils/api-client';
import { encodeUrlParams, urlParamsFromUrl } from 'utils/functions';

interface IUseTotalMembers {
  date: string;
  ordering?: string;
  enabled?: boolean;
  csvExport?: string;
  limit?: string;
}

interface QueryKeyType {
  pageParam?: UrlParams;
  queryKey?: (string | undefined | string[])[];
}

function useTotalMembers({
  date,
  ordering,
  enabled = true,
  csvExport,
  limit = '20',
}: IUseTotalMembers): UseInfiniteQueryResult<AxiosResponse<PaginatedMemberReportList>> {
  const fetchTotalMembers = useCallback(
    ({
      pageParam = { limit, offset: '0' },
    }: QueryKeyType): Promise<AxiosResponse<PaginatedMemberReportList>> => {
      const params = pageParam;

      params.date = date;

      if (ordering) {
        params.ordering = ordering.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`).concat(',pk');
      }

      return client.get(`reports/total-members?${encodeUrlParams(params)}`);
    },
    [limit, date, ordering],
  );

  return useInfiniteQuery(['total-members', date, ordering, csvExport], fetchTotalMembers, {
    getNextPageParam: page => {
      return page.data.next ? urlParamsFromUrl(page.data.next) : undefined;
    },
    enabled: enabled && date !== null,
    staleTime: 86400000, // 1 day
  });
}

export default useTotalMembers;
